<script>
  import { onMount } from "svelte";
  import { useNavigate } from "svelte-navigator";
  import { useUrlParams } from "@stores/urlStore.js";
  import { fetchWithSession } from "@utils/fetchWithSession.js";
  import Header from "lib/components/Header.svelte";
  import { Pagination, SectionWrapper, Accordion, Tooltip, Select, Button} from "paxray-components-svelte";
  import SmallKpi from "lib/components/smallKPI.svelte";
  import LeftOverlayDrawer from "lib/components/common/LeftOverlayDrawer.svelte";
  import { ROUTES } from "@utils/routes.js";
  import SurveyHierarchyTree from "./components/SurveyHierarchyTree.svelte";

  import {
    DataTable,
    Loading,
    Toolbar,
    ToolbarContent,
    ToolbarSearch
} from "carbon-components-svelte";
  import Modal from "lib/components/Modal.svelte";
  import { ARROW_LEFT_ICON, ARROW_RIGHT_ICON, PREVIEW_ICON, RESET_ICON } from '@utils/icons.js';

  export let survey;
  export let detailMode = false;

  const navigate = useNavigate();
  const urlParams = useUrlParams();

  let loading = true;
  let loadingData = true;

  let analisysData;
  let totalAnswers = 0;
  let totalUsers = 0;
  let invitations = 0;
  let totalSurveyTasks = 0;
  let totalClusters = 0;
  let surveyStatus = null;
  let surveyName = "";
  let endSurveyModalOpen = false;
  let endingSurvey = false;
  let sendInvitationsModalOpen = false;
  let sendingInvitations = false;
  let sendInvitationsError = "";
  let pendingInvitations = 0;
  let startOfSurvey;
  let endOfSurvey;
  let questions;
  let notResponse = false;
  let dataError = false;
  let fullscreen = false;
  let selectedElementType = "area";
  let selectedElementContext = "";
  let hierarchySearch = "";
  let hierarchyDrawerOpen = false;
  let selectedHierarchyTaskIds = [];
  let commentDraft = "";
  let clusters = [];
  let minimumClusterPotential = 0;
  let clusterPage = 0;
  let clustersLoading = false;
  let clustersError = "";
  let regeneratingCluster = false;
  let updatingMeasure = "";
  let commentsLoading = false;
  let commentsSaving = false;
  let commentsError = "";
  let deleteMeasureModalOpen = false;
  let pendingDeleteMeasureIndex = null;
  let preparedSurveyId = null;
  let taskBusinessCases = {};
  let retryingAnalysis = false;
  let analysisRetryMessage = "";
  let analysisPreparation = {
    running: false,
    completed: 0,
    total: 0,
    areaCompleted: 0,
    areaTotal: 0,
    failed: 0,
  };
  let elementSelected;
  let loadedDetailKey = "";

  let filteredData;
  let selectedData = { department: null, area: null, task: null };

  let filters = {
    kpi: "",
    areas: [],
    departments: [],
    tasks: [],
  };
  let selectedDepartment = "";
  let selectedArea = "";
  let selectedTask = "";
  let departmentAreaSearch = {};
  let departmentAreaPages = {};
  let departmentAreaPageSizes = {};

  let details = [];
  let detailsPage = 1;
  const detailsPerPage = 5;
  const departmentAreaHeaders = [
    { key: "name", value: "Area" },
    { key: "tasks", value: "Tasks" },
    { key: "coverage", value: "Coverage" },
    { key: "executionHours", value: "Task time p.a." },
    { key: "realisticPotential", value: "Realistic potential" },
    { key: "score", value: "Score" },
    { key: "details", value: "Details" },
  ];

  let totalDepartments = [];
  let totalAreas = [];
  let totalTasks = [];
  let filterAreas = [];
  let filterDepartments = [];
  let filterTasks = [];

  let estimatedTime = 0;
  let totalColleages = 0;

  let scores = [];
  let selectedComments = [];

  $: syncSingleSelectFilters(selectedDepartment, selectedArea, selectedTask);
  $: setFilteredData(filters);
  $: detailAreaId = $urlParams.area;
  $: participationMode = $urlParams.view === "participation";
  $: showParticipation = participationMode || surveyStatus !== "FINISHED";
  $: detailDepartmentName = $urlParams.department || "";
  $: if (detailMode && filteredData && detailAreaId) syncDetailElementFromUrl();
  $: setDetails(elementSelected);
  $: overviewAreas = (filteredData || []).flatMap((department) =>
    (department.areas || []).map((area) => ({ ...area, departmentName: department.name })),
  );
  $: visibleClusters = [...clusters]
    .filter(
      (cluster) =>
        Number(cluster.business_case?.cluster_potential_hours || 0) >=
        minimumClusterPotential,
    )
    .sort(
      (a, b) =>
        Number(b.business_case?.cluster_potential_hours || 0) -
        Number(a.business_case?.cluster_potential_hours || 0),
    );
  $: if (clusterPage > visibleClusters.length) clusterPage = 0;
  $: isClusterSummary = clusterPage === 0;
  $: activeCluster = clusterPage > 0 ? visibleClusters[clusterPage - 1] || null : null;
  $: activeBusinessCase = activeCluster?.business_case || null;
  $: detailEmptyCanvas = detailMode && !elementSelected;
  $: if (!showParticipation && surveyStatus === "FINISHED" && detailEmptyCanvas) hierarchyDrawerOpen = true;
  $: summaryPotential = sumClusterMetric(visibleClusters, "cluster_potential_hours");
  $: summaryAddressable = sumClusterMetric(visibleClusters, "addressable_hours");
  $: summaryExpectedImpact = visibleClusters.reduce(
    (sum, cluster) => sum + getClusterExpectedImpact(cluster),
    0,
  );
  $: summaryInputCount = new Set(
    visibleClusters.flatMap((cluster) =>
      (cluster.members || []).map((member) => member.id),
    ),
  ).size;
  $: summaryTopMeasures = visibleClusters
    .flatMap((cluster) =>
      (cluster.measures || []).map((measure) => ({
        ...measure,
        clusterTitle: cluster.title,
      })),
    )
    .sort(
      (a, b) =>
        Number(b.expected_impact_hours || 0) -
        Number(a.expected_impact_hours || 0),
    )
    .slice(0, 4);
  $: displayedPotential = isClusterSummary
    ? (visibleClusters.length ? summaryPotential : estimatedTime)
    : activeBusinessCase?.cluster_potential_hours || estimatedTime;
  $: clusterMembers = activeCluster?.members || [];
  $: detailsPageCount = Math.max(1, Math.ceil(clusterMembers.length / detailsPerPage));
  $: if (detailsPage > detailsPageCount) detailsPage = detailsPageCount;
  $: paginatedDetailInputs = clusterMembers.slice(
    (detailsPage - 1) * detailsPerPage,
    detailsPage * detailsPerPage,
  );

  $: if (questions && filteredData && !dataError && !notResponse)
    setScoresToShow();

  function setScoresToShow() {
    let departmentsWithScores = analisysData.filter(
      (department) => Object.keys(department.scores).length > 0,
    );
    if (!departmentsWithScores.length) {
      scores = [];
      return;
    }
    scores = questions
      .filter((question) =>
        Object.entries(departmentsWithScores[0].scores).some(
          ([key, value]) => question.id === key,
        ),
      )
      .map((score) => {
        return {
          id: score.id,
          name: score.title,
          answers: score.answers || [],
        };
      });
  }

  function setDateToShow(datetime) {
    if (!datetime) return "–";
    const date = new Date(datetime);
    if (Number.isNaN(date.getTime())) return "–";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).format(date);
  }

  function setCompletedSurveys(invitations, totalUsers) {
    if (!invitations || !totalUsers) return "0";
    return `${Math.round((totalUsers / invitations) * 100)}`;
  }

  function syncSingleSelectFilters(department, area, task) {
    const nextDepartments = department ? [department] : [];
    const nextAreas = area ? [area] : [];
    const nextTasks = task ? [task] : [];
    if (
      filters.departments[0] === nextDepartments[0] &&
      filters.departments.length === nextDepartments.length &&
      filters.areas[0] === nextAreas[0] &&
      filters.areas.length === nextAreas.length &&
      filters.tasks[0] === nextTasks[0] &&
      filters.tasks.length === nextTasks.length
    ) {
      return;
    }
    filters = {
      ...filters,
      departments: nextDepartments,
      areas: nextAreas,
      tasks: nextTasks,
    };
  }

  function resetAreaAndTaskFilters() {
    selectedArea = "";
    selectedTask = "";
  }

  function resetTaskFilter() {
    selectedTask = "";
  }

  function resetFilters() {
    filters = {
      kpi: "",
      areas: [],
      departments: [],
      tasks: [],
    };
    selectedDepartment = "";
    selectedArea = "";
    selectedTask = "";
  }

  function getElementScore(element) {
    const value = filters.kpi
      ? element?.scores?.[filters.kpi]
      : element?.total_score;
    return Number(value) || 0;
  }

  function formatScore(element) {
    return getElementScore(element).toFixed(1);
  }

  function formatHours(value) {
    const hours = Math.round(Number(value) || 0);
    if (Math.abs(hours) >= 1000000) return `${(hours / 1000000).toFixed(1)}m`;
    if (Math.abs(hours) >= 1000) return `${(hours / 1000).toFixed(1)}k`;
    return hours.toLocaleString("en-US");
  }

  function sumClusterMetric(items, key) {
    return items.reduce(
      (sum, cluster) => sum + Number(cluster.business_case?.[key] || 0),
      0,
    );
  }

  function getClusterExpectedImpact(cluster) {
    return (cluster?.measures || []).reduce(
      (sum, measure) => sum + Number(measure.expected_impact_hours || 0),
      0,
    );
  }

  function formatMonthlyHours(value) {
    return (Number(value || 0) / 12).toLocaleString("en-US", {
      maximumFractionDigits: 1,
    });
  }

  function formatWorkdays(value) {
    return (Number(value || 0) / 8).toLocaleString("en-US", {
      maximumFractionDigits: 1,
    });
  }

  function getClusterEvidenceStrength(cluster) {
    const members = cluster?.members || [];
    if (!members.length) return "Low";
    const completeInputs = members.filter(
      (member) =>
        Number(member.occurrences_per_year || 0) > 0 &&
        Number(member.time_lost_minutes || 0) > 0 &&
        Number(member.number_of_colleagues || 0) > 0,
    ).length;
    const completeness = completeInputs / members.length;
    if (members.length >= 3 && completeness >= 0.75) return "High";
    if (members.length >= 2 && completeness >= 0.5) return "Medium";
    return "Low";
  }

  function getClusterEvidenceSummary(cluster) {
    const members = cluster?.members || [];
    if (!members.length) return "No quantitative inputs";
    const average = (key) => {
      const values = members
        .filter((member) => member[key] != null && member[key] !== "")
        .map((member) => Number(member[key]))
        .filter(Number.isFinite);
      return values.length ? Math.round(values.reduce((sum, value) => sum + value, 0) / values.length) : 0;
    };
    const durationSeconds = average("task_duration_seconds") || average("task_duration_minutes") * 60;
    return `${members.length} inputs · ${average("task_occurrences_per_month")} executions/month · ${Math.floor(durationSeconds / 60)}:${String(durationSeconds % 60).padStart(2, "0")} min:sec · ${average("number_of_colleagues")} colleagues · ${average("standard_case_share_percent")}% standard cases`;
  }

  function getTaskPotential(task) {
    if (taskBusinessCases[task?.id]) {
      return taskBusinessCases[task.id].potential;
    }
    const taskDetails = (task.task_surveys?.[0]?.answers || []).flatMap(
      (answer) => answer.details || [],
    );
    if (!taskDetails.length) return 0;
    const frequencyFactors = { Rarely: .1, Sometimes: .35, Often: .7, "Almost always": 1 };
    const impactFactors = { "<10%": .05, "10-25%": .175, "25-50%": .375, ">50%": .65 };
    const concreteLosses = taskDetails.map((detail) => {
      const occurrences = Number(detail.occurrences_per_year) ||
        Number(detail.task_occurrences_per_month || 0) * 12 * Number(frequencyFactors[detail.occurrence_frequency] || 0);
      const durationSeconds = Number(detail.task_duration_seconds || Number(detail.task_duration_minutes || 0) * 60);
      const lostSeconds = durationSeconds * Number(impactFactors[detail.impact_on_task_time] || 0);
      return occurrences * lostSeconds / 3600 * Math.max(Number(detail.number_of_colleagues || 0), 0);
    }).filter((value) => value > 0);
    if (concreteLosses.length) {
      const reportedLoss = concreteLosses.reduce((sum, value) => sum + value, 0) / concreteLosses.length;
      const executionCap = getTaskExecutionHours(task);
      return Math.round(executionCap ? Math.min(reportedLoss, executionCap) : reportedLoss);
    }
    const monthlyHours = taskDetails.reduce((sum, detail) => sum + Number(detail.avg_time || 0), 0) / taskDetails.length;
    const occurrences = taskDetails.reduce((sum, detail) => sum + Number(detail.occurrences_per_year || 0), 0) / taskDetails.length;
    const timeLostMinutes = taskDetails.reduce((sum, detail) => sum + Number(detail.time_lost_minutes || 0), 0) / taskDetails.length;
    const colleagues = taskDetails.reduce((sum, detail) => sum + Number(detail.number_of_colleagues || 0), 0) / taskDetails.length;
    const reportedLoss = occurrences && timeLostMinutes
      ? occurrences * timeLostMinutes / 60 * colleagues
      : monthlyHours * colleagues * 12;
    return Math.round(Math.min(reportedLoss, colleagues * 1200));
  }

  function getElementPotential(element) {
    if (element?.areas) return element.areas.reduce((sum, area) => sum + getElementPotential(area), 0);
    if (element?.tasks) return element.tasks.reduce((sum, task) => sum + getTaskPotential(task), 0);
    return getTaskPotential(element || {});
  }

  function getTaskExecutionHours(task) {
    const estimates = (task?.task_surveys?.[0]?.answers || [])
      .map((answer) => (answer.details || []).find((detail) =>
        Number(detail.task_occurrences_per_month) > 0 &&
        Number(detail.task_duration_seconds || Number(detail.task_duration_minutes || 0) * 60) > 0
      ))
      .filter(Boolean)
      .map((detail) =>
        Number(detail.task_occurrences_per_month) * 12 *
        Number(detail.task_duration_seconds || Number(detail.task_duration_minutes || 0) * 60) / 3600 *
        Math.max(Number(detail.number_of_colleagues) || 1, 1)
      );
    if (!estimates.length) return 0;
    return estimates.reduce((sum, hours) => sum + hours, 0) / estimates.length;
  }

  function getElementExecutionHours(element) {
    if (element?.areas) return element.areas.reduce((sum, area) => sum + getElementExecutionHours(area), 0);
    if (element?.tasks) return element.tasks.reduce((sum, task) => sum + getTaskExecutionHours(task), 0);
    return getTaskExecutionHours(element || {});
  }

  function getElementCoverage(element) {
    if (!invitations) return 0;
    return Math.min(100, Math.round(((element?.all_user_answer_ids?.length || 0) / invitations) * 100));
  }

  function getFeasibility(element) {
    const readiness = scores.find((score) => score.name.toLowerCase().includes("ai readiness"));
    const value = readiness ? Number(element?.scores?.[readiness.id] || 0) : getElementScore(element);
    return Math.min(100, Math.round((value / 5) * 100));
  }

  function getRealisticPotential(element) {
    if (!element?.tasks && taskBusinessCases[element?.id]) {
      return taskBusinessCases[element.id].realistic;
    }
    if (element?.areas) {
      return element.areas.reduce((sum, area) => sum + getRealisticPotential(area), 0);
    }
    if (element?.tasks) {
      return element.tasks.reduce((sum, task) => sum + getRealisticPotential(task), 0);
    }
    const baseShare = 0.35 + getFeasibility(element) / 200;
    const details = (element?.task_surveys?.[0]?.answers || []).flatMap((answer) => answer.details || []);
    const standardShares = details
      .map((detail) => detail.standard_case_share_percent)
      .filter((value) => value != null)
      .map(Number);
    const standardCaseCeiling = standardShares.length
      ? standardShares.reduce((sum, value) => sum + value, 0) / standardShares.length / 100
      : 1;
    return Math.round(getElementPotential(element) * Math.min(baseShare, standardCaseCeiling));
  }

  function getSurveyTasks() {
    return (analisysData || []).flatMap((department) =>
      (department.areas || []).flatMap((area) =>
        (area.tasks || []).filter((task) => task.all_user_answer_ids?.length > 0),
      ),
    );
  }

  function getResponseCount(element) {
    return new Set(element?.all_user_answer_ids || []).size;
  }

  function getResponsePercentage(element, denominator = invitations) {
    return denominator ? Math.round((getResponseCount(element) / denominator) * 100) : 0;
  }

  function getSurveyMeta() {
    return fetchWithSession(`/api/knextractor/surveys/${survey}`)
      .then((response) => response.json().then((data) => ({ response, data })))
      .then(({ response, data }) => {
        if (!response.ok) throw new Error("Survey status could not be loaded");
        surveyStatus = data.status;
        surveyName = data.name;
      })
      .catch(() => {
        dataError = true;
      });
  }

  async function endSurvey() {
    if (endingSurvey) return;
    endingSurvey = true;
    try {
      const response = await fetchWithSession(`/api/knextractor/surveys/${survey}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          enabled: false,
          end_date: new Date().toISOString().slice(0, 10),
        }),
      });
      if (!response.ok) throw new Error("Ending survey failed");
      endSurveyModalOpen = false;
      surveyStatus = "FINISHED";
      preparedSurveyId = null;
      await prepareTaskAnalyses(true);
    } catch (error) {
      dataError = true;
    } finally {
      endingSurvey = false;
    }
  }

  async function sendInvitations() {
    if (sendingInvitations || pendingInvitations <= 0) return;
    sendingInvitations = true;
    sendInvitationsError = "";
    try {
      const response = await fetchWithSession(
        `/api/knextractor/surveys/${survey}/send-emails`,
        { method: "POST" },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Invitation sending failed");
      pendingInvitations = Math.max(0, pendingInvitations - Number(data.emails_sent || 0));
      surveyStatus = "RUNNING";
      sendInvitationsModalOpen = false;
    } catch (error) {
      sendInvitationsError = error.message || "Invitation sending failed";
    } finally {
      sendingInvitations = false;
    }
  }

  async function prepareTaskAnalyses(force = false) {
    if (!survey || analysisPreparation.running) return;
    if (!force && preparedSurveyId === survey) return;
    if (!force) {
      try {
        const snapshotResponse = await fetchWithSession(
          `/api/knextractor/analysis/${survey}/snapshot`,
        );
        const snapshotData = await snapshotResponse.json();
        if (!snapshotResponse.ok) throw new Error(snapshotData.error || "Analysis status could not be loaded");
        if (!snapshotData.allowed) {
          preparedSurveyId = survey;
          return;
        }
        if (snapshotData.ready && snapshotData.snapshot) {
          preparedSurveyId = survey;
          taskBusinessCases = snapshotData.snapshot.task_metrics || {};
          filteredData = filteredData ? [...filteredData] : filteredData;
          totalClusters = snapshotData.snapshot.cluster_total || 0;
          analysisPreparation = {
            running: false,
            completed: snapshotData.snapshot.task_total || 0,
            total: snapshotData.snapshot.task_total || 0,
            areaCompleted: snapshotData.snapshot.area_total || 0,
            areaTotal: snapshotData.snapshot.area_total || 0,
            failed: 0,
          };
          return;
        }
      } catch (error) {
        analysisPreparation = {
          ...analysisPreparation,
          total: getSurveyTasks().length,
          failed: 1,
        };
        return;
      }
    }
    const tasks = getSurveyTasks();
    preparedSurveyId = survey;
    const areas = (analisysData || []).flatMap((department) => department.areas || []);
    analysisPreparation = {
      running: true,
      completed: 0,
      total: tasks.length,
      areaCompleted: 0,
      areaTotal: areas.length,
      failed: 0,
    };
    let nextTaskIndex = 0;

    async function worker() {
      while (nextTaskIndex < tasks.length) {
        const task = tasks[nextTaskIndex++];
        try {
          const response = await fetchWithSession(
            `/api/knextractor/analysis/${survey}/improvement-clusters/task/${task.id}`,
          );
          const clusters = await response.json();
          if (!response.ok) throw new Error(clusters.error || "Task analysis failed");
          const analyzedClusters = clusters.filter((cluster) => cluster.business_case);
          if (analyzedClusters.length) taskBusinessCases = {
            ...taskBusinessCases,
            [task.id]: {
              potential: analyzedClusters.reduce(
                (sum, cluster) => sum + Number(cluster.business_case?.cluster_potential_hours || 0),
                0,
              ),
              realistic: analyzedClusters.reduce(
                (sum, cluster) => sum + Number(cluster.business_case?.addressable_hours || 0),
                0,
              ),
            },
          };
        } catch (error) {
          analysisPreparation = {
            ...analysisPreparation,
            failed: analysisPreparation.failed + 1,
          };
        } finally {
          analysisPreparation = {
            ...analysisPreparation,
            completed: analysisPreparation.completed + 1,
          };
        }
      }
    }

    await Promise.all(Array.from({ length: Math.min(3, tasks.length) }, worker));
    filteredData = filteredData ? [...filteredData] : filteredData;
    let nextAreaIndex = 0;
    async function areaWorker() {
      while (nextAreaIndex < areas.length) {
        const area = areas[nextAreaIndex++];
        try {
          const response = await fetchWithSession(
            `/api/knextractor/analysis/${survey}/improvement-clusters/area/${area.id}`,
          );
          const result = await response.json();
          if (!response.ok) throw new Error(result.error || "Area analysis failed");
        } catch (error) {
          analysisPreparation = {
            ...analysisPreparation,
            failed: analysisPreparation.failed + 1,
          };
        } finally {
          analysisPreparation = {
            ...analysisPreparation,
            areaCompleted: analysisPreparation.areaCompleted + 1,
          };
        }
      }
    }
    await Promise.all(Array.from({ length: Math.min(3, areas.length) }, areaWorker));
    if (analysisPreparation.failed === 0) {
      try {
        const response = await fetchWithSession(
          `/api/knextractor/analysis/${survey}/snapshot`,
          { method: "POST" },
        );
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Analysis could not be finalized");
        taskBusinessCases = data.snapshot?.task_metrics || taskBusinessCases;
        filteredData = filteredData ? [...filteredData] : filteredData;
        totalClusters = data.snapshot?.cluster_total || totalClusters;
      } catch (error) {
        analysisPreparation = { ...analysisPreparation, failed: 1 };
      }
    }
    analysisPreparation = { ...analysisPreparation, running: false };
  }

  async function retryFailedAnalyses() {
    if (retryingAnalysis || analysisPreparation.running) return;
    retryingAnalysis = true;
    analysisRetryMessage = "";
    await prepareTaskAnalyses(true);
    analysisRetryMessage = analysisPreparation.failed
      ? `${analysisPreparation.failed} analysis ${analysisPreparation.failed === 1 ? "item is" : "items are"} still unavailable.`
      : "Analysis completed successfully.";
    retryingAnalysis = false;
  }

  function scoreTone(element) {
    const value = getElementScore(element);
    if (value >= 4) return "score_good";
    if (value < 3) return "score_risk";
    return "score_medium";
  }

  function getDimensionScoreValue(element, score) {
    const value = element?.scores?.[score.id];
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  function isHigherDimensionScoreBetter(score) {
    return score.id === "ai_readiness";
  }

  function dimensionScoreTone(score, value) {
    if (value === null) return "score_medium";
    if (isHigherDimensionScoreBetter(score)) {
      if (value >= 4) return "score_good";
      if (value < 3) return "score_risk";
      return "score_medium";
    }
    if (value <= 2) return "score_good";
    if (value > 3) return "score_risk";
    return "score_medium";
  }

  function formatDimensionScore(value) {
    return value === null ? "–" : value.toFixed(1);
  }

  function dimensionScoreTooltip(score, value) {
    const direction = isHigherDimensionScoreBetter(score)
      ? "Higher values are better for this dimension."
      : "Lower values are better for this dimension.";
    if (value === null) return `${direction} No score is available yet.`;
    const tone = dimensionScoreTone(score, value);
    const rating = tone === "score_good"
      ? "good"
      : tone === "score_risk"
        ? "critical"
        : "neutral";
    return `${direction} Current score: ${value.toFixed(1)} (${rating}).`;
  }

  function overallScoreTooltip(element) {
    return `Higher values are better for the overall score. Current score: ${formatScore(element)}.`;
  }

  function getDepartmentTaskCount(department) {
    return (department.areas || []).reduce(
      (total, area) => total + (area.tasks?.length || 0),
      0,
    );
  }

  function getDepartmentAreaRows(department) {
    return (department.areas || []).map((area) => ({
      id: `area-${area.id}`,
      name: area.name,
      score: formatScore(area),
      executionHours: `${formatHours(getElementExecutionHours(area))} h`,
      realisticPotential: `${formatHours(getRealisticPotential(area))} h`,
      coverage: `${getElementCoverage(area)}%`,
      tasks: area.tasks?.length || 0,
      details: "",
      areaElement: area,
      departmentName: department.name,
    }));
  }

  function getDepartmentAreaSearch(departmentId) {
    return departmentAreaSearch[departmentId] || "";
  }

  function getFilteredDepartmentAreaRows(department) {
    const search = getDepartmentAreaSearch(department.id).trim().toLowerCase();
    const rows = getDepartmentAreaRows(department);
    if (!search) return rows;
    return rows.filter((row) =>
      departmentAreaHeaders.some((header) =>
        String(row[header.key] ?? "").toLowerCase().includes(search),
      ),
    );
  }

  function getPagedDepartmentAreaRows(department) {
    const rows = getFilteredDepartmentAreaRows(department);
    const page = getDepartmentAreaPage(department.id);
    const pageSize = getDepartmentAreaPageSize(department.id);
    return rows.slice((page - 1) * pageSize, page * pageSize);
  }

  function getDepartmentAreaPage(departmentId) {
    return departmentAreaPages[departmentId] || 1;
  }

  function getDepartmentAreaPageSize(departmentId) {
    return departmentAreaPageSizes[departmentId] || 5;
  }

  function setDepartmentAreaSearch(departmentId, value) {
    departmentAreaSearch = { ...departmentAreaSearch, [departmentId]: value };
    departmentAreaPages = { ...departmentAreaPages, [departmentId]: 1 };
  }

  function setDepartmentAreaPage(departmentId, value) {
    departmentAreaPages = { ...departmentAreaPages, [departmentId]: value };
  }

  function setDepartmentAreaPageSize(departmentId, value) {
    departmentAreaPageSizes = { ...departmentAreaPageSizes, [departmentId]: value };
    departmentAreaPages = { ...departmentAreaPages, [departmentId]: 1 };
  }

  function getHierarchySource() {
    return (analisysData || [])
      .map((department) => ({
        ...department,
        areas: (department.areas || [])
          .map((area) => ({
            ...area,
            tasks: (area.tasks || []).filter(
              (task) =>
                task.task_surveys?.length > 0 &&
                task.all_user_answer_ids?.length > 0,
            ),
          }))
          .filter((area) => area.tasks.length > 0),
      }))
      .filter((department) => department.areas.length > 0);
  }

  function getTaskIds(element) {
    if (element.tasks) return element.tasks.map((task) => task.id);
    return (element.areas || []).flatMap((area) =>
      area.tasks.map((task) => task.id),
    );
  }

  function isTaskSelected(taskId) {
    return selectedHierarchyTaskIds.includes(taskId);
  }

  function isGroupSelected(element) {
    const taskIds = getTaskIds(element);
    return taskIds.length > 0 && taskIds.every(isTaskSelected);
  }

  function isGroupPartiallySelected(element) {
    const taskIds = getTaskIds(element);
    const selectedCount = taskIds.filter(isTaskSelected).length;
    return selectedCount > 0 && selectedCount < taskIds.length;
  }

  function setTaskSelection(taskIds, selected) {
    const nextSelection = new Set(selectedHierarchyTaskIds);
    taskIds.forEach((taskId) =>
      selected ? nextSelection.add(taskId) : nextSelection.delete(taskId),
    );
    selectedHierarchyTaskIds = [...nextSelection];
    setFilteredData(filters);
  }

  function toggleTaskSelection(task, selected) {
    setTaskSelection([task.id], selected);
  }

  function toggleGroupSelection(element, selected) {
    setTaskSelection(getTaskIds(element), selected);
  }

  function getHierarchyData() {
    const query = hierarchySearch.trim().toLowerCase();
    const source = getHierarchySource();
    if (!query) return source;
    return source
      .map((department) => {
        const departmentMatch = department.name.toLowerCase().includes(query);
        const areas = (department.areas || [])
          .map((area) => {
            const areaMatch = area.name.toLowerCase().includes(query);
            const tasks = (area.tasks || []).filter((task) =>
              task.name.toLowerCase().includes(query),
            );
            return departmentMatch || areaMatch || tasks.length
              ? { ...area, tasks: departmentMatch || areaMatch ? area.tasks : tasks }
              : null;
          })
          .filter(Boolean);
        return departmentMatch || areas.length ? { ...department, areas } : null;
      })
      .filter(Boolean);
  }

  function selectDepartment(department) {
    selectedData = { department: department.id, area: null, task: null };
    elementSelected = department;
  }

  function getSurveyDetailHref(area, departmentName = "") {
    const params = new URLSearchParams({
      survey,
      area: String(area.id),
    });
    if (departmentName) params.set("department", departmentName);
    return `${ROUTES.knex.potentialScope.surveyDetail}?${params.toString()}`;
  }

  function openAreaDetailView(area, departmentName = "") {
    navigate(getSurveyDetailHref(area, departmentName));
  }

  function handleHierarchyOpen(event) {
    const { element, type, context } = event.detail;
    if (detailMode && type === "area") {
      openElementDetails(element, type, context);
      openAreaDetailView(element, context);
      return;
    }
    openElementDetails(element, type, context);
  }

  function syncDetailElementFromUrl() {
    const match = (filteredData || [])
      .flatMap((department) =>
        (department.areas || []).map((area) => ({
          area,
          departmentName: department.name,
        })),
      )
      .find((item) => String(item.area.id) === String(detailAreaId));
    if (!match) return;
    const nextKey = `area-${match.area.id}`;
    if (loadedDetailKey === nextKey) return;
    loadedDetailKey = nextKey;
    openElementDetails(
      match.area,
      "area",
      detailDepartmentName || match.departmentName,
    );
  }

  function openElementDetails(element, type, context = "") {
    if (!detailMode) {
      if (type === "area") openAreaDetailView(element, context);
      selectedElementType = type;
      selectedElementContext = context;
      elementSelected = element;
      selectedData =
        type === "area"
          ? { department: null, area: element.id, task: element.tasks }
          : { department: null, area: null, task: element.id };
      return;
    }
    selectedElementType = type;
    selectedElementContext = context;
    elementSelected = element;
    commentDraft = "";
    detailsPage = 1;
    clusterPage = 0;
    minimumClusterPotential = 0;
    clusters = [];
    selectedComments = [];
    clustersError = "";
    commentsError = "";
    selectedData =
      type === "area"
        ? { department: null, area: element.id, task: element.tasks }
        : { department: null, area: null, task: element.id };
    setDetails(element);
    loadImprovementData(element, type);
  }

  async function loadImprovementData(element, type) {
    const entityId = element.id;
    clustersLoading = true;
    clustersError = "";
    commentsLoading = true;
    commentsError = "";
    fetchWithSession(
      `/api/knextractor/analysis/${survey}/improvement-clusters/${type}/${entityId}`,
    )
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Cluster generation failed");
        if (elementSelected?.id === entityId) clusters = data;
      })
      .catch((error) => {
        if (elementSelected?.id === entityId) clustersError = error.message;
      })
      .finally(() => {
        if (elementSelected?.id === entityId) clustersLoading = false;
      });
    fetchWithSession(
      `/api/knextractor/analysis/${survey}/comments/${type}/${entityId}`,
    )
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Comments could not be loaded");
        if (elementSelected?.id === entityId) selectedComments = data;
      })
      .catch((error) => {
        if (elementSelected?.id === entityId) commentsError = error.message;
      })
      .finally(() => {
        if (elementSelected?.id === entityId) commentsLoading = false;
      });
  }

  function changeClusterPage(step) {
    clusterPage = Math.max(0, Math.min(clusterPage + step, visibleClusters.length));
    detailsPage = 1;
  }

  function setMinimumClusterPotential(event) {
    minimumClusterPotential = Math.max(0, Number(event.currentTarget.value) || 0);
    clusterPage = 0;
    detailsPage = 1;
  }

  async function regenerateSuggestedApproach() {
    if (!activeCluster || regeneratingCluster) return;
    regeneratingCluster = true;
    clustersError = "";
    try {
      const response = await fetchWithSession(
        `/api/knextractor/analysis/${survey}/improvement-clusters/${activeCluster.id}/regenerate`,
        { method: "POST" },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Regeneration failed");
      clusters = clusters.map((cluster) =>
        cluster.id === data.id ? data : cluster,
      );
    } catch (error) {
      clustersError = error.message;
    } finally {
      regeneratingCluster = false;
    }
  }

  async function updateMeasure(index, method) {
    if (!activeCluster || updatingMeasure) return;
    updatingMeasure = `${method}-${index}`;
    clustersError = "";
    try {
      const response = await fetchWithSession(
        `/api/knextractor/analysis/${survey}/improvement-clusters/${activeCluster.id}/measures/${index}`,
        { method },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Measure could not be updated");
      clusters = clusters.map((cluster) => cluster.id === data.id ? data : cluster);
    } catch (error) {
      clustersError = error.message;
    } finally {
      updatingMeasure = "";
    }
  }

  function requestMeasureDelete(index) {
    pendingDeleteMeasureIndex = index;
    deleteMeasureModalOpen = true;
  }

  async function confirmMeasureDelete() {
    if (pendingDeleteMeasureIndex === null) return;
    const index = pendingDeleteMeasureIndex;
    await updateMeasure(index, "DELETE");
    if (!clustersError) {
      deleteMeasureModalOpen = false;
      pendingDeleteMeasureIndex = null;
    }
  }

  async function addComment() {
    const comment = commentDraft.trim();
    if (!comment || !elementSelected || commentsSaving) return;
    const entityType = selectedElementType;
    const entityId = elementSelected.id;
    commentsSaving = true;
    commentsError = "";
    try {
      const response = await fetchWithSession(
        `/api/knextractor/analysis/${survey}/comments/${entityType}/${entityId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ comment }),
        },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Comment could not be saved");
      selectedComments = data;
      commentDraft = "";
    } catch (error) {
      commentsError = error.message;
    } finally {
      commentsSaving = false;
    }
  }

  function getInitials(name) {
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  }

  function setTotalScore(element) {
    let totalScore = (
      Object.values(element).reduce(
        (sum, value) => sum + parseFloat(value),
        0,
      ) / scores.length
    ).toFixed(1);
    return totalScore;
  }

  function setFilterAreas() {
    filterAreas = [];
    filteredData.forEach((department) => {
      department.areas.forEach((area) => {
        if (area.tasks.some((task) => task.task_surveys.length > 0))
          filterAreas = [...filterAreas, { id: area.id, text: area.name }];
      });
    });
    filterAreas = [...filterAreas];
    if (loading) totalAreas = [...filterAreas];
  }
  function setFilterDepartments() {
    filterDepartments = [];
    filteredData.forEach((department) => {
      filterDepartments = [
        ...filterDepartments,
        { id: department.id, text: department.name },
      ];
    });
    filterDepartments = [...filterDepartments];
    if (loading) totalDepartments = [...filterDepartments];
  }

  function setFilterTask() {
    const nextFilterTasks = [];
    getHierarchySource().forEach((department) => {
      department.areas.forEach((area) => {
        if (
          filters.areas.length === 0 ||
          filters.areas.includes(String(area.id))
          //|| area.tasks.some((task) => filters.tasks.includes(task.id))
        ) {
          area.tasks.forEach((task) => {
            if (
              nextFilterTasks.find(
                (ftask) =>
                  ftask.id === task.id || task.task_surveys.length === 0,
              )
              //|| (filters.areas.length > 0 && !filters.tasks.includes(task.id))
            )
              return;
            nextFilterTasks.push({ id: task.id, text: task.name });
          });
        }
      });
    });
    const newTaskFilters = nextFilterTasks.filter((task) =>
      filters.tasks.includes(String(task.id)),
    );
    if (filters.tasks.length !== newTaskFilters.length) {
      filters = { ...filters, tasks: newTaskFilters.map((task) => task.id) };
      return;
    }
    const tasksChanged = nextFilterTasks.length !== filterTasks.length ||
      nextFilterTasks.some((task, index) => task.id !== filterTasks[index]?.id);
    if (tasksChanged) filterTasks = nextFilterTasks;
    if (loading) totalTasks = [...filterTasks];
  }

  function setElementScores(element) {
    let elementScores = {};
    scores.forEach((score) => {
      elementScores[score.id] = (
        element.reduce(
          (sum, item) => sum + (parseFloat(item.scores[score.id]) || 0),
          0,
        ) / element.length
      ).toFixed(1);
    });
    return elementScores;
  }

  function setFilteredData(filters) {
    if (loadingData) return;
    filteredData = analisysData
      .map((department) => {
        let filteredDepartment = { ...department };
        if (department.areas.length > 0) {
          filteredDepartment.areas = department.areas
            .map((area) => {
              let filteredArea = { ...area };
              if ((filters.tasks && filters.tasks.length > 0) || area.tasks) {
                filteredArea.tasks = area.tasks.filter(
                  (task) =>
                    (filters.tasks?.length === 0 ||
                      filters.tasks?.includes(String(task.id))) &&
                    selectedHierarchyTaskIds.includes(task.id) &&
                    task.all_user_answer_ids.length > 0,
                );
              }

              return filteredArea;
            })
            .filter((area) => area.tasks.length > 0)
            .map((filteredArea) => {
              const areaTasks = filteredArea.tasks || [];
              const areaScores = setElementScores(areaTasks);
              return {
                ...filteredArea,
                all_user_answer_ids: [
                  ...new Set(
                    areaTasks.flatMap((task) => task.all_user_answer_ids),
                  ),
                ],
                scores: areaScores,
                total_score: setTotalScore(areaScores),
              };
            });

          if (filters.areas && filters.areas.length > 0) {
            filteredDepartment.areas = filteredDepartment.areas.filter((area) =>
              filters.areas.includes(String(area.id)),
            );
          }

          if (filters.tasks && filters.tasks.length > 0) {
            filteredDepartment.areas = filteredDepartment.areas.filter(
              (area) => area.tasks && area.tasks.length > 0,
            );
          }
        }
        return filteredDepartment;
      })
      .filter((department) => department.areas.length > 0)
      .map((filteredDepartment) => {
        const departmentAreas = filteredDepartment.areas || [];
        const departmentScores = setElementScores(departmentAreas);
        return {
          ...filteredDepartment,
          all_user_answer_ids: [
            ...new Set(
              departmentAreas.flatMap((area) => area.all_user_answer_ids),
            ),
          ],
          scores: departmentScores,
          total_score: setTotalScore(departmentScores),
        };
      });

    if (filters.departments && filters.departments.length > 0) {
      filteredData = filteredData.filter((data) =>
        filters.departments.includes(String(data.id)),
      );
    }
    if (
      (filters.areas && filters.areas.length > 0) ||
      (filters.tasks && filters.tasks.length > 0)
    ) {
      filteredData = filteredData.filter(
        (data) => data.areas && data.areas.length > 0,
      );
    }
    setDetails(elementSelected);
    if (loading) {
      //get the options for the filters above
      if (filteredData.length === 0) {
        notResponse = true;
        loading = false;
        return;
      }
      setFilterDepartments();
      setFilterAreas();
    }

    setFilterTask();
    filteredData = [...filteredData];
    loading = false;
  }

  function setDetails(elementSelected) {
    details = [];
    totalColleages = 0;
    estimatedTime = 0;
    let userspertask = 0;
    let colleaguespertask = 0;
    let timepertask = 0;
    if (!filteredData) return;
    filteredData.forEach((department) =>
      department.areas.forEach((area) => {
        if (!selectedData.area || selectedData.area === area.id) {
          area.tasks.forEach((task) => {
            if (
              task.task_surveys[0] &&
              (!selectedData.task ||
                selectedData.area ||
                selectedData.task === task.id)
            ) {
              userspertask = 0;
              colleaguespertask = 0;
              timepertask = 0;
              task.task_surveys[0].answers.forEach((answer) => {
              if (answer.details) {
                  answer.details.forEach((adetails) => {
                    details = [
                      ...details,
                      {
                        id: answer.id,
                        task: task.name,
                        details: adetails,
                      },
                    ];
                    colleaguespertask = colleaguespertask + adetails.number_of_colleagues;
                    timepertask = timepertask + (
                      adetails.occurrences_per_year && adetails.time_lost_minutes
                        ? adetails.occurrences_per_year * adetails.time_lost_minutes / 60
                        : (adetails.avg_time || 0) * 12
                    );
                    userspertask = userspertask + 1;
                  });
               }
              });
                  if (userspertask > 0) {
                    totalColleages = Math.max(
                      totalColleages,
                      colleaguespertask / userspertask,
                    );
                    estimatedTime = estimatedTime + (timepertask / userspertask) * totalColleages;
                  }
            }
          });
        }
      }),
    );
    totalColleages = Math.round(totalColleages *10) /10;
    estimatedTime = Math.round(estimatedTime * 10) / 10;
  }

  function getQuestions() {
    fetchWithSession("/api/knextractor/questions/")
      .then((response) => response.json())
      .then((data) => {
        questions = data;
        if (analisysData) {
          setScoresToShow();
          setFilteredData(filters);
        }
      });
  }

  async function loadOverview() {
    loading = true;
    loadingData = true;
    dataError = false;
    try {
      const [surveyResponse, questionsResponse, analysisResponse] = await Promise.all([
        fetchWithSession(`/api/knextractor/surveys/${survey}`),
        fetchWithSession("/api/knextractor/questions/"),
        fetchWithSession(`/api/knextractor/analysis/${survey}`),
      ]);
      const [surveyData, questionsData, analysisData] = await Promise.all([
        surveyResponse.json(),
        questionsResponse.json(),
        analysisResponse.json(),
      ]);
      if (!surveyResponse.ok || !questionsResponse.ok || !analysisResponse.ok) {
        throw new Error("Survey overview could not be loaded");
      }

      surveyStatus = surveyData.status;
      surveyName = surveyData.name;
      pendingInvitations = Number(surveyData.pending_invitations || 0);
      questions = Array.isArray(questionsData) ? questionsData : [];
      analisysData = Array.isArray(analysisData) ? analysisData : [];
      if (!analisysData.length) {
        notResponse = true;
        return;
      }

      setScoresToShow();
      selectedHierarchyTaskIds = getHierarchySource().flatMap((department) =>
        department.areas.flatMap((area) => area.tasks.map((task) => task.id)),
      );
      const completedAnswerIds = new Set(
        analisysData.flatMap((department) => department.all_user_answer_ids || []),
      );
      totalUsers = completedAnswerIds.size;
      totalAnswers = completedAnswerIds.size;
      totalSurveyTasks = surveyData.survey_tasks?.length || 0;
      invitations = surveyData.survey_users?.length || 0;
      endOfSurvey = setDateToShow(analisysData[0].survey_end_date);
      startOfSurvey = setDateToShow(analisysData[0].survey_start_date);
      loadingData = false;
      setFilteredData(filters);
      if (surveyStatus === "FINISHED") prepareTaskAnalyses();
    } catch (error) {
      console.error("Survey overview loading failed", error);
      dataError = true;
    } finally {
      loadingData = false;
      loading = false;
    }
  }

  function getAnalisysData() {
    fetchWithSession("/api/knextractor/analysis/" + survey)
      .then((response) => {
        if (!response.ok) {
          dataError = true;
        }
        return response.json();
      })
      .then((data) => {
        analisysData = data;
        if (questions) setScoresToShow();
        selectedHierarchyTaskIds = getHierarchySource().flatMap((department) =>
          department.areas.flatMap((area) => area.tasks.map((task) => task.id)),
        );
        //analisysData = data.department

        const completedAnswerIds = new Set(
          analisysData.flatMap(
            (department) => department.all_user_answer_ids || [],
          ),
        );
        totalUsers = completedAnswerIds.size;
        totalAnswers = completedAnswerIds.size;
        totalSurveyTasks = getSurveyTasks().length;
        invitations = data.reduce(
          (total, department) => total + department.users_added_to_survey,
          0,
        );

        endOfSurvey = setDateToShow(data[0].survey_end_date);
        startOfSurvey = setDateToShow(data[0].survey_start_date);

        setDetails(elementSelected);
        loadingData = false;
        setFilteredData(filters);
        prepareTaskAnalyses();
        // if (data[0].all_user_answer_ids.length === 0) notResponse = true;
        // else if (
        //   data[0].all_user_answer_ids.length >= 0 &&
        //   !data[0].total_score
        // ) {
        //   dataError = true;
        // }
      })
      .catch((error) => {
        dataError = true;
        //loading = false;
      });
  }

  function toggleFullscreen() {
    fullscreen = !fullscreen;
  }

  export async function exportAsImage() {
    const canvasElement = document.querySelector(".main_wrapper > .canvas");
    if (!canvasElement) return;

    const clone = canvasElement.cloneNode(true);

    const classesToHide = ["menu_overlay", "button_wrapper"];

    const copyAndCleanStyles = (source, target) => {
      if (!source || !target) return;

      // CHECK: If the element has one of the prohibited classes,
      // we hide it and do NOT continue processing its children.
      const shouldHide = classesToHide.some((cls) =>
        target.classList.contains(cls),
      );

      if (shouldHide) {
        target.style.display = "none";
        target.style.opacity = "0";
        return; // Stop processing children
      }

      const computed = window.getComputedStyle(source);
      for (const prop of computed) {
        const value = computed.getPropertyValue(prop);
        if (value.includes("url(")) continue;
        target.style[prop] = value;
      }

      const sourceChildren = source.children;
      const targetChildren = target.children;

      for (let i = 0; i < targetChildren.length; i++) {
        copyAndCleanStyles(sourceChildren[i], targetChildren[i]);
      }
    };

    copyAndCleanStyles(canvasElement, clone);

    const rect = canvasElement.getBoundingClientRect();
    const exportWidth = Math.ceil(Math.max(rect.width, canvasElement.scrollWidth));
    const exportHeight = Math.ceil(Math.max(rect.height, canvasElement.scrollHeight));
    clone.style.width = `${exportWidth}px`;
    clone.style.height = `${exportHeight}px`;
    clone.style.overflow = "visible";

    clone
      .querySelectorAll(".score_badge")
      .forEach((element) => {
        element.style.whiteSpace = "nowrap";
        element.style.wordBreak = "normal";
        element.style.overflowWrap = "normal";
      });

    clone.querySelectorAll(".score_badge").forEach((element) => {
      element.style.width = "auto";
      element.style.minWidth = "2.5rem";
      element.style.boxSizing = "border-box";
      element.style.textAlign = "center";
    });

    const serializer = new XMLSerializer();
    const svgString = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${exportWidth}" height="${exportHeight}">
      <foreignObject width="100%" height="100%">
        <div xmlns="http://www.w3.org/1999/xhtml" style="margin:0;padding:0;background:white;width:100%;height:100%;">
          ${serializer.serializeToString(clone)}
        </div>
      </foreignObject>
    </svg>
  `;

    const base64Svg = btoa(unescape(encodeURIComponent(svgString)));
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = 2;
      canvas.width = exportWidth * scale;
      canvas.height = exportHeight * scale;
      const ctx = canvas.getContext("2d");
      ctx.scale(scale, scale);

      try {
        ctx.drawImage(img, 0, 0);
        const pngUrl = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = pngUrl;
        link.download = `SurveyOverview_${Date.now()}.png`;
        link.click();
      } catch (e) {
        console.error("Error in canvas:", e);
      }
    };

    img.src = `data:image/svg+xml;base64,${base64Svg}`;
  }

  onMount(() => {
    loadOverview();
  });
</script>

<Header
  section="Potential Scout"
  projectNameOverride={surveyName}
  projectHref={ROUTES.dam.root}
  area="Potential Scout"
  areaHref={`${ROUTES.knex.potentialScope.dashboard}?survey=${survey}`}
  areaClickable={false}
  breadcrumb={participationMode ? "Monitor Participation" : detailMode ? "Survey Detail" : "Survey Overview"}
/>

{#if surveyStatus === "FINISHED" && !participationMode}
  <LeftOverlayDrawer title="Hierarchy" bind:open={hierarchyDrawerOpen}>
    <SurveyHierarchyTree
      withAccordion={false}
      {loading}
      departments={getHierarchyData()}
      {selectedData}
      {elementSelected}
      {formatScore}
      {scoreTone}
      {isGroupSelected}
      {isGroupPartiallySelected}
      {isTaskSelected}
      on:search={(event) => (hierarchySearch = event.detail)}
      on:toggleGroup={(event) =>
        toggleGroupSelection(event.detail.element, event.detail.selected)}
      on:toggleTask={(event) =>
        toggleTaskSelection(event.detail.task, event.detail.selected)}
      on:selectDepartment={(event) => selectDepartment(event.detail)}
      on:openElement={handleHierarchyOpen}
    />
  </LeftOverlayDrawer>
{/if}

<SectionWrapper
  fullscreen={fullscreen || showParticipation || !detailMode || detailEmptyCanvas}
  fitScreen={showParticipation || detailEmptyCanvas}
  fitContent={!showParticipation && !detailEmptyCanvas}
  leftOverlayOffset={!showParticipation && surveyStatus === "FINISHED" ? "2.25rem" : "0rem"}
>
  <div
    slot="main"
    class="main_wrapper"
    on:mousedown={(e) => {
      if (detailMode) return;
      selectedData = {
        department: null,
        area: null,
        task: null,
      };
      elementSelected = null;
    }}
  >
    <div
      class="canvas"
      class:pending_canvas={showParticipation}
      class:detail_empty_canvas={detailEmptyCanvas}
    >
      {#if loading || !surveyStatus}
        <Loading></Loading>
      {:else if dataError}
        <div class="not_response">
          Error in the data.<br />Please contact support.
        </div>
      {:else if showParticipation}
        <div class="participation_overview">
          <section class="participation_hero">
            <div>
              <span class="survey_live_badge">{surveyStatus}</span>
              <h1>{surveyName || "Survey participation"}</h1>
              <p>{surveyStatus === "FINISHED"
                ? "Final participation results for this completed survey."
                : "Participation progress is available while the survey is active. Detailed analysis starts only after the survey has been ended."}</p>
            </div>
            <div class="participation_actions">
              <button
                type="button"
                on:click={() => window.open(
                  `${ROUTES.knex.survey}?preview=${survey}`,
                  "_blank",
                  "noopener,noreferrer",
                )}
              >Preview</button>
              {#if surveyStatus !== "FINISHED" && pendingInvitations > 0}
                <button type="button" on:click={() => (sendInvitationsModalOpen = true)}>
                  Send now
                </button>
              {/if}
              {#if surveyStatus !== "FINISHED"}
                <button type="button" on:click={() => (endSurveyModalOpen = true)}>End survey</button>
              {/if}
            </div>
          </section>

          <section class="participation_kpis">
            <article><span>Recipients</span><strong>{invitations}</strong><small>Invited participants</small></article>
            <article><span>Responses</span><strong>{totalUsers}</strong><small>Completed submissions</small></article>
            <article><span>Response rate</span><strong>{setCompletedSurveys(invitations, totalUsers)}%</strong><small>{Math.max(invitations - totalUsers, 0)} still outstanding</small></article>
            <article><span>Tasks</span><strong>{totalSurveyTasks}</strong><small>Included in this survey</small></article>
          </section>

          <section class="participation_structure">
            <header>
              <div><h2>Participation by topic</h2><p>Submission coverage only. No scores or analysis details are shown before completion.</p></div>
            </header>
            {#each analisysData || [] as department}
              <details open class="participation_department">
                <summary>
                  <strong>{department.name}</strong>
                  <span>{getResponseCount(department)} responses</span>
                  <b>{getResponsePercentage(department, invitations)}%</b>
                </summary>
                <div class="participation_areas">
                  {#each department.areas || [] as area}
                    <article>
                      <header><strong>{area.name}</strong><span>{getResponseCount(area)} responses</span></header>
                      <div class="participation_bar"><span style:width={`${getResponsePercentage(area, invitations)}%`}></span></div>
                      <div class="participation_tasks">
                        {#each area.tasks || [] as task}
                          <div>
                            <span>{task.name}</span>
                            <small>{getResponseCount(task)} / {invitations}</small>
                          </div>
                        {/each}
                      </div>
                    </article>
                  {/each}
                </div>
              </details>
            {/each}
          </section>
        </div>
      {:else if notResponse}
        <div class="not_response">
          No responses have been recorded for this survey yet.
        </div>
      {:else if detailMode}
        <div class="survey_detail_page">
          {#if !detailAreaId}
            <div class="survey_detail_empty_state">
              Select an area in the hierarchy to open its details.
            </div>
          {:else if !elementSelected}
            <Loading></Loading>
          {:else}
            <div class="overview_details_modal survey_detail_layout">
              <section class="details_inputs_column">
                <header class="details_column_header">
                  <div class="label_with_tooltip">
                    <h3>Opportunity clusters</h3>
                    <Tooltip text="Grouped improvement topics based on the selected area or task responses." />
                  </div>
                  <div class="cluster_header_controls">
                    <label class="cluster_potential_filter">
                      <div class="label_with_tooltip">
                        <span>Min. addressable potential</span>
                        <Tooltip text="Hide clusters below this annual addressable potential in hours." />
                      </div>
                      <span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={minimumClusterPotential}
                          aria-label="Minimum cluster potential in hours per year"
                          on:input={setMinimumClusterPotential}
                        />
                        h/year
                      </span>
                    </label>
                    <span class="cluster_count detail_pill">
                      {visibleClusters.length === clusters.length
                        ? clusters.length
                        : `${visibleClusters.length} / ${clusters.length}`}
                      {clusters.length === 1 ? "cluster" : "clusters"}
                    </span>
                  </div>
                </header>
                {#if visibleClusters.length > 0}
                  <div class="cluster_navigation">
                    <Button
                      kind="secondary"
                      size="small"
                      icon={ARROW_LEFT_ICON}
                      iconDescription="Previous cluster"
                      disabled={clusterPage === 0}
                      on:click={() => clusterPage !== 0 && changeClusterPage(-1)}
                    />
                    <span>{isClusterSummary
                      ? "Overview"
                      : `Cluster ${clusterPage} of ${visibleClusters.length}`}</span>
                    <Button
                      kind="secondary"
                      size="small"
                      icon={ARROW_RIGHT_ICON}
                      iconDescription="Next cluster"
                      disabled={clusterPage === visibleClusters.length}
                      on:click={() =>
                        clusterPage !== visibleClusters.length && changeClusterPage(1)}
                    />
                  </div>
                {/if}
                <div class="details_input_list">
                  {#if clustersLoading}
                    <div class="details_empty">Loading saved analysis...</div>
                  {:else if clustersError}
                    <div class="cluster_error">
                      <span>{clustersError}</span>
                      <Button
                        text="Retry"
                        kind="outline"
                        size="field"
                        on:click={() => loadImprovementData(elementSelected, selectedElementType)}
                      />
                    </div>
                  {:else if details.length === 0}
                    <div class="details_empty">
                      No qualitative improvement inputs were submitted for this selection.
                    </div>
                  {:else if isClusterSummary && visibleClusters.length > 0}
                    <section class="management_summary">
                      <header class="management_summary_intro">
                        <div class="label_with_tooltip">
                          <h4>Potential overview</h4>
                          <Tooltip text="Summary across all visible opportunity clusters for the current hierarchy selection." />
                        </div>
                        <p>
                          {visibleClusters.length} problem {visibleClusters.length === 1 ? "cluster represents" : "clusters represent"}
                          {formatHours(summaryPotential)} hours of annual friction. The current analysis considers
                          {formatHours(summaryAddressable)} hours realistically addressable and links concrete measures
                          to {formatHours(summaryExpectedImpact)} hours of expected annual impact.
                        </p>
                      </header>

                      <div class="management_kpi_grid">
                        <article>
                          <div class="metric_label label_with_tooltip">
                            <span>Identified potential</span>
                            <Tooltip text="Estimated annual time lost in all visible clusters before feasibility is applied." />
                          </div>
	                          <strong>{formatHours(summaryPotential)} <small>h/year</small></strong>
	                          <p>{formatMonthlyHours(summaryPotential)} h/month · {formatWorkdays(summaryPotential)} workdays/year</p>
	                        </article>
	                        <article class="addressable_case">
                          <div class="metric_label label_with_tooltip">
                            <span>Addressable potential</span>
                            <Tooltip text="Estimated annual hours that could realistically be addressed by the suggested measures." />
                          </div>
	                          <strong>{formatHours(summaryAddressable)} <small>h/year</small></strong>
	                          <p>{summaryPotential ? Math.round(summaryAddressable / summaryPotential * 100) : 0}% of identified potential</p>
	                        </article>
	                      </div>

	                      <div class="management_summary_meta">
	                        <span class="detail_pill"><strong>{summaryInputCount}</strong> inputs</span>
	                        <span class="detail_pill"><strong>{visibleClusters.length}</strong> clusters</span>
	                        <span class="detail_pill"><strong>{summaryTopMeasures.length}</strong> measures</span>
	                      </div>

	                      <section class="summary_section">
	                        <header>
                          <div class="label_with_tooltip">
                            <strong>Priority clusters</strong>
                            <Tooltip text="The visible clusters with the highest addressable annual potential." />
                          </div>
	                          <span class="detail_pill">Start here</span>
	                        </header>
                        <div class="summary_cluster_list">
                          {#each visibleClusters.slice(0, 3) as cluster, index}
                            <button type="button" on:click={() => (clusterPage = index + 1)}>
                              <span>{index + 1}</span>
                              <div>
                                <strong>{cluster.title}</strong>
                                <small>{getClusterEvidenceStrength(cluster)} evidence · {(cluster.members || []).length} inputs</small>
                              </div>
                              <em>{formatHours(cluster.business_case?.addressable_hours)} h/year</em>
                            </button>
                          {/each}
                        </div>
                      </section>
                    </section>
                  {:else if !activeCluster}
                    <div class="details_empty">
                      {clusters.length
                        ? "No clusters meet the minimum potential."
                        : "No clusters are available."}
                    </div>
                  {:else}
                    <article class="cluster_summary_card">
	                      <small>Cluster overview</small>
	                      <h4>{activeCluster.title}</h4>
                      <p>{activeCluster.summary}</p>
                    </article>
                    {#if activeBusinessCase}
                      <section class="cluster_business_case">
                        <header>
                          <div class="label_with_tooltip">
                            <strong>Business case</strong>
                            <Tooltip text="Estimated effort impact and feasible improvement potential for this cluster." />
                          </div>
                          <div class="label_with_tooltip evidence_label" title={activeBusinessCase.assumptions?.join(" ") || ""}>
                            <small>{getClusterEvidenceSummary(activeCluster)} · {getClusterEvidenceStrength(activeCluster)} evidence</small>
                            <Tooltip text="Evidence is based on the number and consistency of employee inputs in this cluster." />
                          </div>
	                        </header>
	                        <div class="business_case_grid">
	                          <article>
                            <div class="metric_label label_with_tooltip">
                              <span>Identified friction</span>
                              <Tooltip text="Estimated annual hours lost because of the problems grouped in this cluster." />
                            </div>
	                            <strong>{formatHours(activeBusinessCase.cluster_potential_hours)} <small>h / year</small></strong>
	                            <p>Estimated time lost through the identified friction</p>
	                          </article>
	                          <article class="addressable_case">
                            <div class="metric_label label_with_tooltip">
                              <span>Addressable potential</span>
                              <Tooltip text="Part of the identified friction that is realistically addressable." />
                            </div>
	                            <strong>{formatHours(activeBusinessCase.addressable_hours)} <small>h / year</small></strong>
	                            <p>{activeBusinessCase.addressable_percent}% of cluster potential</p>
                          </article>
                        </div>
                      </section>
                    {/if}
                    <div class="cluster_measures_heading">
                      <div class="label_with_tooltip">
                        <strong>Recommended {activeCluster.measures?.length === 1 ? "measure" : "measures"}</strong>
                        <Tooltip text="Concrete actions generated from the employee inputs in this cluster." />
                      </div>
	                      <Button
	                        text={regeneratingCluster ? "Generating..." : "Regenerate"}
                        kind="outline"
                        size="field"
                        disabled={regeneratingCluster}
                        on:click={regenerateSuggestedApproach}
                      />
                    </div>
                    {#if activeCluster.measures?.length}
                      {#each activeCluster.measures as measure, measureIndex}
                        <article class="cluster_approach_card">
                          <header>
                            <h4>{measure.title}</h4>
                            <div class="measure_actions">
                              <div class="detail_pill label_with_tooltip">
                                <span>{formatHours(measure.expected_impact_hours)} h/year impact</span>
                                <Tooltip text="Estimated annual hours this measure could save if implemented." />
                              </div>
                              <button
                                type="button"
                                class:liked={measure.liked}
                                disabled={Boolean(updatingMeasure)}
                                title={measure.liked ? "Remove like" : "Like measure"}
                                on:click={() => updateMeasure(measureIndex, "POST")}
                              >{measure.liked ? "♥" : "♡"}</button>
                              <button
                                type="button"
                                class="delete_measure"
                                disabled={Boolean(updatingMeasure)}
                                title="Delete measure"
                                on:click={() => requestMeasureDelete(measureIndex)}
                              >×</button>
                            </div>
                          </header>
                          <p>{measure.description}</p>
                          {#if measure.evidence}
                            <div class="measure_evidence"><strong>Evidence</strong><span>{measure.evidence}</span></div>
                          {/if}
                        </article>
                      {/each}
                    {:else}
                      <div class="details_empty">No suggested measures remaining.</div>
                    {/if}
                    <div class="cluster_members_heading">
                      <div class="label_with_tooltip">
                        <strong>Supporting inputs</strong>
                        <Tooltip text="Original employee inputs that support this opportunity cluster." />
                      </div>
	                      <span class="detail_pill">{clusterMembers.length}</span>
                    </div>
                    {#each paginatedDetailInputs as detail}
                      <article class="details_input_card">
                        <header>
                          <div>
                            <small>{detail.task}</small>
                            <h4>{detail.problem_short_name || "Improvement opportunity"}</h4>
                          </div>
                          <div class="detail_badges">
	                            <span class="detail_pill">{detail.occurrences_per_year || 0}×/year · {detail.time_lost_minutes || 0} min</span>
	                            <span class="detail_pill">{detail.number_of_colleagues || 0} people</span>
                          </div>
                        </header>
                        {#if detail.problem_desc}
                          <p>{detail.problem_desc}</p>
                        {/if}
                      </article>
                    {/each}
                  {/if}
                </div>
                {#if activeCluster && clusterMembers.length > 0}
                  <div class="details_pagination">
                    <button
                      type="button"
                      disabled={detailsPage === 1}
                      on:click={() => (detailsPage -= 1)}
                    >←</button>
                    <span>
                      {(detailsPage - 1) * detailsPerPage + 1}–{Math.min(
                        detailsPage * detailsPerPage,
                        clusterMembers.length,
                      )} of {clusterMembers.length}
                    </span>
                    <button
                      type="button"
                      disabled={detailsPage === detailsPageCount}
                      on:click={() => (detailsPage += 1)}
                    >→</button>
                  </div>
                {/if}
              </section>

            </div>
          {/if}
        </div>
      {:else}
        <div class="overview_content" class:preparing={analysisPreparation.running}>
          <section class="filter_button_wrapper">
            <div class="primary_kpi_filter">
              {#key scores}
                <Select
                items={scores.map((kpi) => ({ value: String(kpi.id), text: kpi.name }))}
                bind:selected={filters.kpi}
                color="dark"
                placeholder="All KPIs"
                />
              {/key}
            </div>
            <div class="context_filters">
              {#key filterDepartments}
                <Select
                items={filterDepartments.map((department) => ({ value: String(department.id), text: department.text }))}
                bind:selected={selectedDepartment}
                color="dark"
                placeholder="All Departments"
                />
              {/key}
              {#key filterAreas}
                <Select
                items={filterAreas.map((area) => ({ value: String(area.id), text: area.text }))}
                bind:selected={selectedArea}
                color="dark"
                placeholder="All Areas"
                />
              {/key}
              {#key filterTasks}
                <Select
                items={filterTasks.map((task) => ({ value: String(task.id), text: task.text }))}
                bind:selected={selectedTask}
                color="dark"
                placeholder="All Tasks"
                />
              {/key}
            </div>
            <Button
              kind="secondary"
              size="field"
              icon={RESET_ICON}
              iconDescription="Reset filters"
              on:click={resetFilters}
            />
          </section>
          {#if analysisPreparation.failed > 0 && !analysisPreparation.running}
            <div class="analysis_retry">
              <button type="button" disabled={retryingAnalysis} on:click={retryFailedAnalyses}>
                {retryingAnalysis ? "Retrying…" : `Retry ${analysisPreparation.failed} failed`}
              </button>
              {#if analysisRetryMessage}
                <small class="analysis_retry_message">{analysisRetryMessage}</small>
              {/if}
            </div>
          {/if}
          <div class="departments_stack">
            {#each filteredData as department}
              <div class="department_section">
                <div class="table_heading department_table_heading">
                  <h5 title={department.name}>{department.name}</h5>
                  <div class="department_header_details">
                    <span>{department.areas.length} Areas · {getDepartmentTaskCount(department)} Tasks · {department.all_user_answer_ids?.length || 0} Participants</span>
                    <span>Improvement potential <strong>{formatHours(getElementPotential(department))} h</strong></span>
                    <span>Realistic potential <strong>{formatHours(getRealisticPotential(department))} h</strong></span>
                    <span>Coverage <strong>{getElementCoverage(department)}%</strong></span>
                  </div>
                </div>

                  <div class="department_table">
                    <DataTable
                      class="custom-table survey-area-table"
                      headers={departmentAreaHeaders}
                      rows={getPagedDepartmentAreaRows(department)}
                      sortable
                    >
                      <Toolbar>
                        <ToolbarContent>
                          <ToolbarSearch
                            persistent
                            value={getDepartmentAreaSearch(department.id)}
                            placeholder="Search areas..."
                            on:input={(event) =>
                              setDepartmentAreaSearch(department.id, event.target.value)}
                            on:clear={() =>
                              setDepartmentAreaSearch(department.id, "")}
                          />
                        </ToolbarContent>
                      </Toolbar>
                      <span slot="cell" let:cell let:row>
                        {#if cell.key === "name" && row.areaElement}
                          <button
                            type="button"
                            class="table_link"
                            title={row.name}
                            on:click|stopPropagation={() =>
                              openElementDetails(row.areaElement, "area", row.departmentName)}
                          >
                            {row.name}
                          </button>
                        {:else if cell.key === "score"}
                          <span
                            class="score_badge {scoreTone(row.areaElement)}"
                          >
                            {cell.value}
                          </span>
                        {:else if cell.key === "details" && row.areaElement}
                          <div class="details_action_cell">
                            <Button
                              kind="secondary"
                              size="small"
                              icon={PREVIEW_ICON}
                              iconDescription={`Open details for ${row.name}`}
                              on:click={(event) => {
                                event.stopPropagation();
                                openElementDetails(row.areaElement, "area", row.departmentName);
                              }}
                            />
                          </div>
                        {:else}
                          <span class="table_cell_value" title={String(cell.value ?? "")}>
                            {cell.value ?? ""}
                          </span>
                        {/if}
                      </span>
                    </DataTable>
                    {#if getFilteredDepartmentAreaRows(department).length > 5}
                      <Pagination
                        dark
                        page={getDepartmentAreaPage(department.id)}
                        pageSize={getDepartmentAreaPageSize(department.id)}
                        pageSizes={[5, 10, 25]}
                        totalItems={getFilteredDepartmentAreaRows(department).length}
                        on:update={(event) => {
                          setDepartmentAreaPage(department.id, event.detail.page);
                          setDepartmentAreaPageSize(department.id, event.detail.pageSize);
                        }}
                      />
                    {/if}
                  </div>
              </div>
            {/each}
          </div>
        </div>
      {/if}
    </div>
  </div>

  <svelte:fragment slot="menu">
    {#if detailMode && !elementSelected}
      <div class="hierarchy_panel hierarchy_hint_panel">
        <p>Please select an area in the hierarchy.</p>
      </div>
    {:else if detailMode && elementSelected}
      <div class="hierarchy_panel">
        <Accordion open={true}>
          <span slot="heading" class="side_accordion_heading">
            <strong>Current selection</strong>
          </span>
          <div class="side_kpi_list">
            {#if selectedElementContext}
              <SmallKpi
                kpi={selectedElementType === "task" ? "Parent" : "Department"}
                value={selectedElementContext}
              />
            {/if}
            <SmallKpi
              kpi={selectedElementType === "task" ? "Task" : selectedElementType === "department" ? "Department" : "Area"}
              value={elementSelected.name}
            />
            <SmallKpi>
              <span slot="kpi" class="label_with_tooltip">
                <span>Score</span>
                <Tooltip text={overallScoreTooltip(elementSelected)} />
              </span>
              <strong slot="value" class="dimension_score_badge {scoreTone(elementSelected)}">
                {formatScore(elementSelected)}
              </strong>
            </SmallKpi>
          </div>
        </Accordion>
        <Accordion open={true}>
          <span slot="heading" class="side_accordion_heading">
            <strong>Scores</strong>
          </span>
          <div class="side_kpi_list score_kpi_list">
            <SmallKpi>
              <span slot="kpi" class="label_with_tooltip">
                <span>Overall score</span>
                <Tooltip text={overallScoreTooltip(elementSelected)} />
              </span>
              <strong slot="value" class="dimension_score_badge {scoreTone(elementSelected)}">
                {formatScore(elementSelected)}
              </strong>
            </SmallKpi>
            {#each scores as score}
              {@const value = getDimensionScoreValue(elementSelected, score)}
              <SmallKpi>
                <span slot="kpi" class="label_with_tooltip">
                  <span>{score.name}</span>
                  <Tooltip text={dimensionScoreTooltip(score, value)} />
                </span>
                <strong slot="value" class="dimension_score_badge {dimensionScoreTone(score, value)}">
                  {formatDimensionScore(value)}
                </strong>
              </SmallKpi>
            {/each}
          </div>
        </Accordion>
        <Accordion open={false}>
          <span slot="heading" class="side_accordion_heading">
            <strong>KPIs</strong>
          </span>
          <div class="side_kpi_list">
            <SmallKpi
              kpi="Participants"
              value={elementSelected.all_user_answer_ids?.length || 0}
            />
            <SmallKpi
              kpi="Task time p.a."
              value={`${formatHours(getElementExecutionHours(elementSelected))} h`}
            />
            <SmallKpi
              kpi="Potential hours"
              value={`${formatHours(displayedPotential)} h`}
            />
            <SmallKpi
              kpi="Colleagues"
              value={totalColleages}
            />
          </div>
        </Accordion>
        <Accordion open={false}>
          <span slot="heading" class="side_accordion_heading">
            <strong>Comments</strong>
          </span>
          <section class="details_comments_column score_comments_column">
            <div class="details_comment_list">
              {#if commentsError}
                <div class="comment_error">{commentsError}</div>
              {:else if commentsLoading}
                <div class="details_empty">Loading comments...</div>
              {:else if selectedComments.length === 0}
                <div class="comments_empty">
                  <span>○</span>
                  <strong>No comments yet</strong>
                  <p>Start the conversation and collaborate with your team.</p>
                </div>
              {:else}
                {#each selectedComments as comment (comment.id)}
                  <article class="detail_comment">
                    <span class="comment_avatar">{getInitials(comment.personName)}</span>
                    <div>
                      <header>
                        <strong>{comment.personName}</strong>
                        <small>{comment.date}</small>
                      </header>
                      <p>{comment.comment}</p>
                    </div>
                  </article>
                {/each}
              {/if}
            </div>
            <div class="comment_composer">
              <textarea
                rows="4"
                maxlength="250"
                bind:value={commentDraft}
                placeholder="Add a comment..."
              ></textarea>
              <div>
                <small>{commentDraft.length}/250</small>
                <Button
                  text={commentsSaving ? "Saving..." : "Add comment"}
                  kind="secondary"
                  size="field"
                  disabled={!commentDraft.trim() || commentsSaving}
                  on:click={addComment}
                />
              </div>
            </div>
          </section>
        </Accordion>
      </div>
    {/if}
  </svelte:fragment>
</SectionWrapper>

<Modal
  bind:open={deleteMeasureModalOpen}
  modalClass="measure-delete-modal"
  modalHeading="Delete suggested measure?"
  actions
  width={420}
  primaryButtonText={updatingMeasure ? "Deleting..." : "Delete measure"}
  secondaryButtonText="Cancel"
  primaryButtonValidations={Boolean(updatingMeasure)}
  primaryButtonAction={confirmMeasureDelete}
  secondaryButtonAction={() => {
    deleteMeasureModalOpen = false;
    pendingDeleteMeasureIndex = null;
  }}
>
  <div class="delete_measure_warning">
    <strong>This action cannot be undone.</strong>
    <p>The suggested measure will be permanently removed from this improvement cluster.</p>
  </div>
</Modal>

<Modal
  bind:open={sendInvitationsModalOpen}
  modalClass="send-invitations-modal"
  modalHeading="Send invitations now?"
  actions
  width={440}
  primaryButtonText={sendingInvitations ? "Sending..." : "Send instant"}
  secondaryButtonText="Cancel"
  primaryButtonValidations={sendingInvitations}
  primaryButtonAction={sendInvitations}
  secondaryButtonAction={() => (sendInvitationsModalOpen = false)}
>
  <div class="delete_measure_warning">
    <strong>{pendingInvitations} outstanding invitation{pendingInvitations === 1 ? "" : "s"} will be sent immediately.</strong>
    <p>If necessary, the survey will be opened today. Invitations already sent will not be sent again.</p>
    {#if sendInvitationsError}<p class="orange">{sendInvitationsError}</p>{/if}
  </div>
</Modal>

<Modal
  bind:open={endSurveyModalOpen}
  modalClass="end-survey-modal"
  modalHeading="End survey?"
  actions
  width={440}
  primaryButtonText={endingSurvey ? "Ending survey..." : "End survey"}
  secondaryButtonText="Cancel"
  primaryButtonValidations={endingSurvey}
  primaryButtonAction={endSurvey}
  secondaryButtonAction={() => (endSurveyModalOpen = false)}
>
  <div class="delete_measure_warning">
    <strong>Detailed analysis will start after confirmation.</strong>
    <p>Participants will no longer be able to submit or change answers. Existing responses remain available.</p>
  </div>
</Modal>

<style>
  .main_wrapper {
    display: flex;
    gap: 1rem;
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow-x: hidden;
    position: relative;
  }

  :global(.fit_screen) {
    box-sizing: border-box;
    overflow: hidden;
  }

  :global(.fit_screen_menu) {
    height: calc(100vh - 6rem);
    max-height: calc(100vh - 6rem);
    overflow: hidden;
  }

  .canvas {
    display: block;
    width: 100%;
    height: fit-content;
    min-width: 0;
    min-height: 0;
    overflow: visible;
    background: var(--pax-white);
  }

  .canvas.detail_empty_canvas {
    height: 100%;
    display: grid;
    place-items: center;
  }

  .canvas.pending_canvas {
    background: #fff;
    height: 100%;
    overflow: auto;
  }
  .filter_button_wrapper {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    max-width: 100%;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
  }

  .primary_kpi_filter,
  .context_filters {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .primary_kpi_filter {
    min-width: 0;
    box-sizing: border-box;
    flex: 0 0 auto;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    justify-content: flex-start;
  }

  .context_filters {
    padding-left: 0;
    border-left: 0;
  }

  :global(.filter_button_wrapper .bx--select),
  :global(.filter_button_wrapper .bx--select-input__wrapper),
  :global(.filter_button_wrapper .bx--select-input) {
    width: 12rem;
    min-width: 12rem;
    max-width: 12rem;
    height: 2.5rem;
    box-sizing: border-box;
  }

  .not_response {
    width: 100%;
    align-content: center;
    text-align: center;
    font-size: 2rem;
  }

  .participation_overview {
    width: 100%;
    box-sizing: border-box;
    padding-bottom: 4rem;
  }

  .participation_hero {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
    padding: 1.25rem 1.35rem;
    border: 1px solid #e1e4e8;
    border-radius: 0.75rem;
    background: linear-gradient(120deg, #fff8f4, #fff 70%);
  }

  .survey_live_badge {
    display: inline-flex;
    padding: 0.28rem 0.5rem;
    border-radius: 1rem;
    background: #fff0d0;
    color: #8a6500;
    font-size: 0.58rem;
    font-weight: 700;
  }

  .participation_hero h1 {
    margin: 0.55rem 0 0.35rem;
    font-size: 1.15rem;
  }

  .participation_hero p {
    max-width: 44rem;
    margin: 0;
    color: var(--pax-sub, #5f6770);
    font-size: 0.72rem;
    line-height: 1.5;
  }

  .participation_hero button {
    flex-shrink: 0;
    padding: 0.7rem 1rem;
    border: 0;
    border-radius: 0.5rem;
    background: var(--pax-black);
    color: #fff;
    font-size: 0.7rem;
    font-weight: 700;
    cursor: pointer;
  }

  .participation_actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.55rem;
    flex-shrink: 0;
  }

  .participation_kpis {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.75rem;
    margin-top: 0.8rem;
  }

  .participation_kpis article {
    display: flex;
    flex-direction: column;
    padding: 0.9rem 1rem;
    border: 1px solid #e1e4e8;
    border-radius: 0.65rem;
    background: #fff;
  }

  .participation_kpis span,
  .participation_kpis small {
    color: var(--pax-sub, #5f6770);
    font-size: 0.62rem;
  }

  .participation_kpis strong {
    margin: 0.35rem 0 0.2rem;
    font-size: 1.25rem;
  }

  .participation_structure {
    margin-top: 0.8rem;
    padding: 1rem;
    border: 1px solid #e1e4e8;
    border-radius: 0.7rem;
    background: #fff;
  }

  .participation_structure > header h2,
  .participation_structure > header p {
    margin: 0;
  }

  .participation_structure > header h2 {
    font-size: 0.9rem;
  }

  .participation_structure > header p {
    margin-top: 0.25rem;
    color: var(--pax-sub, #5f6770);
    font-size: 0.65rem;
  }

  .participation_department {
    margin-top: 0.75rem;
    overflow: hidden;
    border: 1px solid #e2e5e9;
    border-radius: 0.6rem;
  }

  .participation_department > summary {
    display: grid;
    grid-template-columns: minmax(8rem, 1fr) auto auto;
    align-items: center;
    gap: 0.7rem;
    padding: 0.7rem 0.8rem;
    cursor: pointer;
  }

  .participation_department > summary > span {
    color: var(--pax-sub, #5f6770);
    font-size: 0.65rem;
  }

  .participation_department > summary b {
    min-width: 3rem;
    text-align: right;
    font-size: 0.72rem;
  }

  .participation_areas {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
    gap: 0.65rem;
    padding: 0.75rem;
    border-top: 1px solid #e8eaed;
    background: #fff;
  }

  .participation_areas > article {
    padding: 0.75rem;
    border: 1px solid #e2e5e9;
    border-radius: 0.55rem;
    background: #fff;
  }

  .participation_areas article > header {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    font-size: 0.68rem;
  }

  .participation_areas article > header span {
    color: var(--pax-sub, #5f6770);
    white-space: nowrap;
    font-size: 0.6rem;
  }

  .participation_bar {
    height: 0.35rem;
    margin: 0.6rem 0;
    overflow: hidden;
    border-radius: 1rem;
    background: #eceef1;
  }

  .participation_bar span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: #43b86a;
  }

  .participation_tasks > div {
    display: flex;
    justify-content: space-between;
    gap: 0.7rem;
    padding: 0.4rem 0;
    border-top: 1px solid #f0f1f2;
    font-size: 0.62rem;
  }

  .participation_tasks small {
    flex-shrink: 0;
    color: var(--pax-sub, #5f6770);
  }

  .menu_overlay {
    position: absolute;
    width: 100%;
    z-index: 5;
    display: flex;
    justify-content: end;
    align-items: center;
    box-sizing: border-box;
    padding: 0.75rem;
    pointer-events: none;
  }

  .bottom {
    bottom: 0;
  }

  .button_wrapper {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    pointer-events: auto;
  }

  .overview_content {
    width: 100%;
    min-width: 50rem;
    box-sizing: border-box;
    padding: 0;
  }

  .analysis_retry {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin-bottom: 0.8rem;
  }

  .analysis_retry > button {
    padding: 0.45rem 0.65rem;
    border: 1px solid #cf4e53;
    border-radius: 0.4rem;
    background: #fff;
    color: #a62f34;
    font-size: 0.62rem;
    cursor: pointer;
  }

  .analysis_retry_message {
    color: #6f4b3e;
    font-size: 0.62rem;
  }

  .overview_content.preparing .departments_stack {
    opacity: 0.45;
    pointer-events: none;
    transition: opacity 0.2s ease;
  }

  .departments_stack {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .department_section {
    overflow: visible;
  }

  .table_heading {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    width: 100%;
    background-color: var(--pax-white);
    border: 1px solid var(--pax-black);
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .table_heading h5 {
    margin: 0;
    color: var(--pax-black);
  }

  .department_header_details {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem 1rem;
    color: #6f7681;
    font-size: 0.85rem;
    line-height: 1.1;
  }

  .department_header_details strong {
    color: var(--pax-black);
  }

  .score_badge {
    flex-shrink: 0;
    min-width: 2.35rem;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    padding: 0.2rem 0.45rem;
    color: var(--pax-black);
    font-size: 0.86rem;
    font-weight: 600;
    line-height: 1;
  }

  .score_good {
    border: 1px solid #8fc99b;
    background: #d9f4df;
  }

  .score_medium {
    background: var(--pax-white);
  }

  .score_risk {
    border: 1px solid #cf8f92;
    background: #f6d5d6;
  }

  .table_link {
    max-width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--pax-black);
    white-space: nowrap;
    font: inherit;
    line-height: 1.3;
    font-weight: 700;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
  }

  .table_link:hover {
    color: var(--pax-orange);
  }

  .table_cell_value {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .details_action_cell {
    display: flex;
    align-items: center;
  }

  :global(.survey-area-table .bx--data-table) {
    table-layout: auto;
  }

  :global(.survey-area-table .bx--data-table th:last-child),
  :global(.survey-area-table .bx--data-table td:last-child) {
    width: 1%;
    white-space: nowrap;
  }

  :global(.survey-area-table .bx--data-table th:last-child .bx--table-sort),
  :global(.survey-area-table .bx--data-table th:last-child .bx--table-header-label) {
    width: max-content;
    min-width: 0;
  }

  .hierarchy_panel {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    overflow-y: auto;
    background: var(--pax-smoke);
  }

  .hierarchy_panel :global(.accordion_wrapper) {
    padding: 1rem;
    border: 1px solid #e2e3e5;
    border-radius: 0.5rem;
  }

  .hierarchy_panel :global(.accordion_content) {
    margin-top: 0.8rem;
    padding-top: 0.8rem;
  }

  .hierarchy_panel .score_comments_column {
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
  }

  .details_empty {
    padding: 1rem;
    border-radius: 0.45rem;
    background: #f6f6f7;
    color: var(--pax-sub, #5f6770);
    text-align: center;
    font-size: 0.75rem;
  }

  :global(.measure-delete-modal .bx--modal-container) {
    width: min(30rem, 92vw) !important;
    max-width: 92vw !important;
  }

  .delete_measure_warning {
    padding: 0.35rem 0.1rem;
  }

  .delete_measure_warning strong {
    font-size: 0.8rem;
  }

  .delete_measure_warning p {
    margin: 0.45rem 0 0;
    color: var(--pax-sub, #5f6770);
    font-size: 0.72rem;
    line-height: 1.5;
  }

  .overview_details_modal {
    display: grid;
    grid-template-columns: 19rem minmax(34rem, 1fr) 24rem;
    gap: 1rem;
    height: min(48rem, 76vh);
    min-height: 0;
    background: var(--pax-white);
  }

  .overview_details_modal > section {
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid var(--pax-black);
    border-radius: 0.75rem;
    background: var(--pax-white);
    box-shadow: none;
  }

  .survey_detail_page {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    height: 100%;
    min-height: 0;
  }

  .survey_detail_empty_state {
    flex: 1;
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--pax-black);
    font-size: 1rem;
    font-weight: 400;
    text-align: center;
  }

  /* Both classes sit on the same element, so this override has to be more
     specific than .overview_details_modal to win the three-column template. */
  .overview_details_modal.survey_detail_layout {
    height: auto;
    min-height: calc(100vh - 13rem);
    grid-template-columns: minmax(0, 1fr);
  }

  .survey_detail_layout > section {
    border: 0;
    border-radius: 0;
  }

  .side_accordion_heading {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .hierarchy_hint_panel {
    padding: 1rem;
    border: 1px solid var(--pax-black);
    background: var(--pax-white);
  }

  .hierarchy_hint_panel p {
    margin: 0;
    font-size: 1rem;
    font-weight: 400;
  }

  .side_kpi_list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-top: 0.75rem;
  }

  .side_kpi_list :global(.kpi_wrapper) {
    width: 100%;
    box-sizing: border-box;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .details_column_header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.9rem;
    border-bottom: 1px solid #e2e3e5;
    background: var(--pax-white);
  }

  .details_column_header h3 {
    margin: 0;
  }

  .details_column_header h3 {
    font-size: 0.85rem;
  }

  .cluster_header_controls {
    display: flex;
    align-items: center;
    gap: 0.65rem;
  }

  .cluster_potential_filter {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    color: var(--pax-sub, #5f6770);
    white-space: nowrap;
    font-size: 0.62rem;
  }

  .cluster_potential_filter > span:last-child {
    display: flex;
    align-items: center;
    gap: 0.3rem;
  }

  .cluster_potential_filter input {
    width: 3.75rem;
    height: 1.75rem;
    box-sizing: border-box;
    padding: 0 0.4rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.35rem;
    background: var(--pax-white);
    color: var(--pax-black);
    font: inherit;
  }

  .cluster_count {
    min-width: auto;
    padding: 0.3rem 0.55rem;
    border-radius: 1rem;
    border: 1px solid var(--pax-black);
    background: var(--pax-black);
    color: var(--pax-white);
    text-align: center;
    white-space: nowrap;
    font-size: 0.65rem;
  }

  .label_with_tooltip {
    min-width: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  .label_with_tooltip h3,
  .label_with_tooltip h4,
  .label_with_tooltip strong,
  .label_with_tooltip span,
  .label_with_tooltip small {
    margin: 0;
  }

  .details_input_list,
  .details_comment_list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 0.75rem;
  }

  .cluster_navigation {
    display: grid;
    grid-template-columns: 2rem minmax(0, 1fr) 2rem;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 0.75rem;
    border-bottom: 1px solid #e2e3e5;
    background: var(--pax-white);
  }

  .cluster_navigation span {
    text-align: center;
    font-size: 0.86rem;
    font-weight: 700;
  }

  .details_pagination button {
    width: 2rem;
    height: 2rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.4rem;
    background: var(--pax-white);
    color: var(--pax-black);
    cursor: pointer;
  }

  .details_pagination button:disabled {
    opacity: 0.35;
    cursor: default;
  }

  .cluster_navigation :global(.button_wrapper) {
    width: 2rem;
    height: 2rem;
  }

  .cluster_navigation :global(.button_wrapper.disable) {
    pointer-events: none;
  }

  .cluster_summary_card,
  .cluster_approach_card {
    padding: 1rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.65rem;
    background: var(--pax-white);
  }

  .measure_evidence {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 0.25rem;
    margin-top: 0.65rem;
    padding-top: 0.55rem;
    border-top: 1px solid #cee7d5;
    color: #4f6657;
    font-size: 0.62rem;
    line-height: 1.45;
  }

  .measure_evidence strong {
    color: #277a43;
  }

  .measure_evidence span {
    min-width: 0;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .cluster_summary_card {
    margin-bottom: 0.55rem;
  }

  .management_summary {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .management_summary_intro {
    padding: 1rem 1.1rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.65rem;
    background: var(--pax-white);
  }

  .management_summary_intro h4 {
    margin: 0.35rem 0 0;
    font-size: 1rem;
  }

  .management_summary_intro p {
    margin: 0.5rem 0 0;
    color: #4d5665;
    font-size: 0.72rem;
    line-height: 1.55;
  }

  .management_kpi_grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.55rem;
  }

  .management_kpi_grid article {
    min-width: 0;
    padding: 0.85rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.55rem;
    background: var(--pax-white);
  }

  .management_kpi_grid .addressable_case {
    background: var(--pax-white);
  }

  .management_kpi_grid span {
    display: block;
    font-size: 0.58rem;
    font-weight: 700;
    text-transform: uppercase;
  }

  .management_kpi_grid strong {
    display: block;
    margin-top: 0.4rem;
    font-size: 1.25rem;
  }

  .management_kpi_grid strong small {
    font-size: 0.58rem;
  }

  .management_kpi_grid p {
    margin: 0.3rem 0 0;
    color: var(--pax-sub, #5f6770);
    font-size: 0.58rem;
    line-height: 1.35;
  }

  .management_summary_meta {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .management_summary_meta span {
    padding: 0.4rem 0.55rem;
    border: 1px solid var(--pax-black);
    border-radius: 1rem;
    color: var(--pax-black);
    font-size: 0.62rem;
  }

  .summary_section {
    padding: 0.8rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.65rem;
    background: var(--pax-white);
  }

  .summary_section > header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.55rem;
    font-size: 0.68rem;
  }

  .summary_section > header span {
    color: var(--pax-sub, #5f6770);
    font-size: 0.58rem;
  }

  .summary_cluster_list {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .summary_cluster_list button {
    min-width: 0;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.65rem;
    padding: 0.65rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.5rem;
    background: var(--pax-white);
    color: var(--pax-black);
    text-align: left;
  }

  .summary_cluster_list button {
    cursor: pointer;
  }

  .summary_cluster_list button:hover {
    border-color: var(--pax-black);
    background: var(--pax-smoke, #f7f8fa);
  }

  .summary_cluster_list button > span {
    width: 1.45rem;
    height: 1.45rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: var(--pax-black);
    color: var(--pax-white);
    font-size: 0.62rem;
    font-weight: 700;
  }

  .summary_cluster_list div {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .summary_cluster_list strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.68rem;
  }

  .summary_cluster_list small {
    color: var(--pax-sub, #5f6770);
    font-size: 0.58rem;
  }

  .summary_cluster_list em {
    color: #238047;
    white-space: nowrap;
    font-size: 0.62rem;
    font-style: normal;
    font-weight: 700;
  }

  .cluster_business_case {
    margin-bottom: 0.75rem;
    overflow: hidden;
    border: 1px solid var(--pax-black);
    border-radius: 0.65rem;
    background: var(--pax-white);
  }

  .cluster_business_case > header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.65rem 0.85rem;
    border-bottom: 1px solid #e7e8ea;
    font-size: 0.68rem;
    text-transform: uppercase;
  }

  .cluster_business_case > header small {
    color: var(--pax-sub, #5f6770);
    font-size: 0.58rem;
    font-weight: 400;
    text-transform: none;
  }

  .business_case_grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.55rem;
    padding: 0.7rem;
  }

  .business_case_grid > article {
    min-width: 0;
    padding: 0.75rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.55rem;
    background: var(--pax-white);
  }

  .business_case_grid > .addressable_case {
    background: var(--pax-white);
  }

  .business_case_grid span,
  .business_case_grid p,
  .business_case_grid small {
    font-size: 0.58rem;
  }

  .business_case_grid .metric_label > span {
    display: block;
    font-weight: 700;
    text-transform: uppercase;
  }

  .business_case_grid > article > strong {
    display: block;
    margin-top: 0.45rem;
    font-size: 1.35rem;
  }

  .business_case_grid p {
    margin: 0.25rem 0 0;
    color: var(--pax-sub, #5f6770);
    line-height: 1.35;
  }

  .addressable_case p {
    color: #26834a;
    font-weight: 700;
  }

  .cluster_summary_card small {
    color: var(--pax-sub, #5f6770);
    font-size: 0.62rem;
  }

  .cluster_summary_card h4,
  .cluster_summary_card p,
  .cluster_approach_card h4,
  .cluster_approach_card p {
    margin: 0;
  }

  .cluster_summary_card h4,
  .cluster_approach_card h4 {
    margin-top: 0.2rem;
    font-size: 0.9rem;
  }

  .cluster_summary_card p,
  .cluster_approach_card p {
    margin-top: 0.55rem;
    color: #474752;
    font-size: 0.76rem;
    line-height: 1.55;
  }

  .cluster_approach_card {
    margin-bottom: 0.55rem;
    border-color: var(--pax-black);
    background: var(--pax-white);
  }

  .cluster_approach_card header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .measure_actions {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  .measure_actions > .detail_pill {
    flex-shrink: 0;
  }

  .measure_actions button {
    width: 1.8rem;
    height: 1.8rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 1px solid var(--pax-black);
    border-radius: 0.4rem;
    background: var(--pax-white);
    color: #515866;
    font-size: 0.9rem;
    cursor: pointer;
  }

  .measure_actions button.liked {
    border-color: var(--pax-orange);
    color: var(--pax-orange);
  }

  .measure_actions button.delete_measure:hover {
    border-color: #d45a5f;
    color: #b52f35;
  }

  .measure_actions button:disabled {
    opacity: 0.45;
    cursor: default;
  }

  .cluster_measures_heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin: 0.1rem 0 0.45rem;
    font-size: 0.7rem;
  }

  .cluster_members_heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.45rem;
    font-size: 0.7rem;
  }

  .cluster_members_heading span {
    min-width: 1.4rem;
    padding: 0.2rem 0.35rem;
    border-radius: 1rem;
    border: 1px solid var(--pax-black);
    background: var(--pax-black);
    color: var(--pax-white);
    text-align: center;
    font-size: 0.6rem;
  }

  .cluster_error {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.75rem;
    border: 1px solid #e7b7b8;
    border-radius: 0.45rem;
    background: #fff1f1;
    color: #8a3033;
    font-size: 0.68rem;
  }

  .details_pagination {
    display: grid;
    grid-template-columns: 2rem minmax(0, 1fr) 2rem;
    align-items: center;
    gap: 0.5rem;
    padding: 0.65rem 0.75rem;
    border-top: 1px solid #e2e3e5;
    background: var(--pax-white);
  }

  .details_pagination span {
    padding: 0.55rem;
    border-radius: 0.4rem;
    background: #f4f4f5;
    text-align: center;
    font-size: 0.68rem;
    font-weight: 700;
  }

  .details_input_card {
    padding: 0.95rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.65rem;
    background: var(--pax-white);
  }

  .details_input_card + .details_input_card {
    margin-top: 0.55rem;
  }

  .details_input_card header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .details_input_card h4,
  .details_input_card p {
    margin: 0;
  }

  .details_input_card h4 {
    margin-top: 0.15rem;
    font-size: 0.75rem;
  }

  .details_input_card small {
    color: var(--pax-sub, #5f6770);
    font-size: 0.62rem;
  }

  .details_input_card p {
    margin-top: 0.65rem;
    color: #474752;
    font-size: 0.7rem;
    line-height: 1.45;
  }

  .detail_badges {
    display: flex;
    gap: 0.3rem;
  }

  .detail_badges span {
    padding: 0.22rem 0.4rem;
    border-radius: 1rem;
    border: 1px solid var(--pax-black);
    background: var(--pax-white);
    color: var(--pax-black);
    white-space: nowrap;
    font-size: 0.58rem;
  }

  .detail_pill {
    min-width: auto;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    padding: 0.28rem 0.5rem;
    border: 1px solid var(--pax-black);
    border-radius: 1rem;
    background: var(--pax-white);
    color: var(--pax-black);
    white-space: nowrap;
    font-size: 0.62rem;
    font-weight: 700;
    line-height: 1;
  }

  .cluster_count.detail_pill,
  .cluster_members_heading .detail_pill {
    background: var(--pax-black);
    color: var(--pax-white);
  }

  .detail_comment {
    display: grid;
    grid-template-columns: 1.8rem minmax(0, 1fr);
    gap: 0.55rem;
    padding: 0.65rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.5rem;
    background: var(--pax-white);
  }

  .detail_comment + .detail_comment {
    margin-top: 0.5rem;
  }

  .comment_avatar {
    width: 1.8rem;
    height: 1.8rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: var(--pax-black);
    color: var(--pax-white);
    font-size: 0.58rem;
    font-weight: 700;
  }

  .detail_comment header {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    font-size: 0.65rem;
  }

  .detail_comment header small {
    color: var(--pax-sub, #5f6770);
  }

  .detail_comment p {
    margin: 0.35rem 0 0;
    color: #474752;
    font-size: 0.68rem;
    line-height: 1.4;
  }

  .comment_composer {
    padding: 0.75rem;
    border-top: 1px solid #e2e3e5;
    background: var(--pax-white);
  }

  .comment_composer textarea {
    width: 100%;
    box-sizing: border-box;
    resize: vertical;
    padding: 0.65rem;
    border: 1px solid #d7d8db;
    border-radius: 0.6rem;
    font: inherit;
    font-size: 0.7rem;
  }

  .comment_composer > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 0.45rem;
  }

  .comment_composer small {
    color: var(--pax-sub, #5f6770);
    font-size: 0.62rem;
  }

  .dimension_score_badge {
    min-width: 2.35rem;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.2rem 0.45rem;
    border-radius: 999px;
    color: var(--pax-black);
    font-size: 0.86rem;
    font-weight: 600;
    line-height: 1;
  }

  .details_inputs_column > .details_column_header {
    min-height: 4.25rem;
    box-sizing: border-box;
    align-items: center;
    padding: 1rem;
  }

  .details_column_header h3 {
    font-size: 0.95rem;
  }

  .cluster_summary_card {
    padding: 1rem 1.1rem;
  }

  .cluster_summary_card h4 {
    margin-top: 0.35rem;
    font-size: 0.95rem;
  }

  .cluster_summary_card p {
    font-size: 0.74rem;
    line-height: 1.55;
  }

  .cluster_business_case > header {
    padding: 0.75rem 0.9rem;
  }

  .business_case_grid {
    gap: 0.7rem;
    padding: 0.8rem;
  }

  .business_case_grid > article {
    min-height: 4.9rem;
    box-sizing: border-box;
    padding: 0.85rem;
  }

  .cluster_measures_heading {
    margin: 0.2rem 0 0.55rem;
  }

  .details_comments_column {
    background: #fff !important;
  }

  .score_comments_column {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid var(--pax-black);
    border-radius: 0.75rem;
    background: var(--pax-white);
  }

  .score_comments_column .details_comment_list {
    flex: 0 1 auto;
    max-height: 18rem;
    overflow-y: auto;
  }

  .score_comments_column .comments_empty {
    height: auto;
    min-height: 0;
    flex: 0 0 auto;
    padding: 1rem;
  }

  .details_comment_list {
    display: flex;
    flex-direction: column;
    padding: 0.85rem;
  }

  .comments_empty {
    min-height: 0;
    flex: 1;
  }

  .comments_empty p {
    width: 100%;
    max-width: none;
    text-align: center;
    white-space: normal;
  }

  .comment_composer {
    flex-shrink: 0;
    padding: 0.85rem;
  }

  .comment_composer textarea {
    min-height: 5.5rem;
    resize: none;
  }

  .comments_empty {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    color: var(--pax-sub, #5f6770);
    text-align: center;
  }

  .comment_error {
    padding: 0.75rem;
    border: 1px solid #e7b7b8;
    border-radius: 0.45rem;
    background: #fff1f1;
    color: #8a3033;
    font-size: 0.68rem;
  }

  .comments_empty > span {
    width: 4rem;
    height: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
    border-radius: 50%;
    background: #fff1ea;
    color: var(--pax-orange);
    font-size: 2rem;
  }

  .comments_empty strong {
    color: var(--pax-black);
    font-size: 0.85rem;
  }

  .comments_empty p {
    width: 100%;
    max-width: 18rem;
    margin: 0.4rem 0 0;
    text-align: center;
    font-size: 0.68rem;
    line-height: 1.5;
    transform: translateX(0.9rem);
  }

  @media (max-width: 78rem) {
    .overview_details_modal {
      grid-template-columns: 14rem minmax(25rem, 1fr) 19rem;
    }
  }
</style>
